import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import moment from "moment";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button } from "antd"; 
import { Link } from "react-router-dom";
import ReportingSelect from "../../../components/ReportingSelect";
import { useGlobalData } from "../../context/GlobalDataContext";  // Import the global context


const AttendanceEmployee = () => {
  const defaultEmployeeId = localStorage.getItem("defaultEmployeeId");
  const defaultEmployeeLabel = localStorage.getItem("defaultEmployeeLabel") || "Select Employee";

  const [data, setData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(
    defaultEmployeeId ? { value: defaultEmployeeId, label: defaultEmployeeLabel } : null
  );
  const [selectedDate, setSelectedDate] = useState(moment());
  const [loading, setLoading] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(parseInt(localStorage.getItem("empid")) || null);
  const [selectedEmployeeLabel, setSelectedEmployeeLabel] = useState(localStorage.getItem("empName") || "Select Employee");
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
    // Check if it's an additional off day (like 3rd Saturday off)
  const {
    holidays,
    weekends,
    weekStart,
    weekEnd,
    additionalOffDays,
    workingHours,
    loading: globalLoading,
  } = useGlobalData();  // Access the global data

  const today = moment(); // Get the current date to compare future dates

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener("click", handleClick);

    return () => {
      maximizeBtn.removeEventListener("click", handleClick);
    };
  }, [isFullScreen]);

  // Fetch attendance data based on employee and date (month and year)
  const fetchData = async (empId, date) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/attendance/list?empid=${empId}&month=${date.month() + 1}&year=${date.year()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const transformedData = response.data.map((attendance) => {
        const attendanceDate = moment(attendance.attendance_date);
        const isHoliday = holidays.includes(attendanceDate.format("YYYY-MM-DD"));
        const isWeekend = weekends.includes(attendanceDate.day());

        let attendanceStatus = null;

        if (attendanceDate.isSameOrBefore(today)) {
          if (attendance.attendance_status === "Checked In") {
            // Display worked hours instead of "Present"
            attendanceStatus = `${attendance.total_hours} hrs`;
          } else {
            attendanceStatus = "Absent";
          }

          if (isHoliday) attendanceStatus = "Holiday";
          else if (isWeekend) attendanceStatus = "Weekend";
          else if (isWeekend) attendanceStatus = "Weekend";
        } else {
          // Future dates, show only holidays or weekends
          if (isHoliday) attendanceStatus = "Holiday";
          else if (isWeekend) attendanceStatus = "Weekend";
        }

        return {
          id: attendance.id,
          attendance_date: attendanceDate,
          attendance_status: attendanceStatus,
          total_hours: attendance.total_hours,
          check_in_time: attendance.check_in_time,
          isHoliday,
          isWeekend,
        };
      });
      setData(transformedData);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedEmployeeId) {
      fetchData(selectedEmployeeId, selectedDate);
    }
  }, [selectedEmployeeId, selectedDate]);

  const handleEmployeeChange = (selectedOption) => {
    if (selectedOption) {
      const { value, label } = selectedOption;
      setSelectedEmployee({ value, label });
      setSelectedEmployeeId(value);
      setSelectedEmployeeLabel(label);
      localStorage.setItem("defaultEmployeeId", value);
      localStorage.setItem("defaultEmployeeLabel", label);
    } else {
      setSelectedEmployee(null);
      setSelectedEmployeeId(null);
      localStorage.removeItem("defaultEmployeeId");
      localStorage.removeItem("defaultEmployeeLabel");
    }
  };

  const handlePrevMonth = () => {
    setSelectedDate(selectedDate.clone().subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setSelectedDate(selectedDate.clone().add(1, "month"));
  };

  // Render calendar grid
  const renderCalendar = () => {
    const startOfMonth = selectedDate.clone().startOf("month").startOf("week");
    const endOfMonth = selectedDate.clone().endOf("month").endOf("week");
    const calendarDays = [];
    let day = startOfMonth.clone();

    while (day.isBefore(endOfMonth, "day")) {
      const dayData = data.find((d) => d.attendance_date.isSame(day, "day"));

      calendarDays.push(
        <div key={day.format("YYYY-MM-DD")} className={`cal_calendar-day ${day.isSame(selectedDate, "month") ? "" : "cal_disabled"} ${dayData?.isHoliday ? "cal_holiday" : ""} ${dayData?.isWeekend ? "cal_weekend" : ""}`}>
          <span className="cal_date-number">{day.format("D")}</span>
          {dayData?.attendance_status ? (
            <div className={`cal_attendance-status ${dayData.attendance_status.includes("hrs") ? "cal_present" : "cal_absent"}`}>
              {dayData.attendance_status}
            </div>
          ) : (
            day.isSameOrBefore(today) ? <div className="cal_attendance-status cal_absent">Absent</div> : null
          )}
        </div>
      );

      day.add(1, "day");
    }

    return calendarDays;
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header page-top-icons">
            <div className="row align-items-center">
              <div className="col-md-4">
                <ReportingSelect
                  name="employee"
                  value={selectedEmployeeId ? { value: selectedEmployeeId, label: selectedEmployeeLabel } : null}
                  onChange={handleEmployeeChange}
                />
              </div>

              <div className="col-md-4">
                <Button className="fa fa-chevron-circle-left" onClick={handlePrevMonth} />
                <span style={{ margin: "0 10px", fontWeight: "bold", fontSize: "16px" }}>
                  {selectedDate.format("MMMM YYYY")}
                </span>
                <Button className="fa fa-chevron-circle-right" onClick={handleNextMonth} />
              </div>

              <div className="col-md-4 float-end ms-auto">
                <div className="d-flex title-head">
                  <div className="view-icons">
                    <Link to="/attendance-weekly" className="grid-view btn btn-link">
                      <i className="fa fa-th" />
                    </Link>
                    <Link to="#" className="grid-view btn btn-link active mx-2" >
                     
                    <i class="fa fa-calendar-check"></i>      
                    </Link>

                    <Link to="/attendance-monthly" className="list-view btn btn-link">
                      <i className="fa fa-bars" />
                    </Link>

                    <Link to="#" className="grid-view btn btn-link">
                      <i className="las la-redo-alt" />
                    </Link>
                    <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                      <i className="las la-expand-arrows-alt" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div id="loader-wrapper-">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
          ) : (
            <div className="cal_calendar-grid">
              <div className="cal_calendar-header-day">Sun</div>
              <div className="cal_calendar-header-day">Mon</div>
              <div className="cal_calendar-header-day">Tue</div>
              <div className="cal_calendar-header-day">Wed</div>
              <div className="cal_calendar-header-day">Thu</div>
              <div className="cal_calendar-header-day">Fri</div>
              <div className="cal_calendar-header-day">Sat</div>
              {renderCalendar()}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AttendanceEmployee;
